import React, { useContext } from "react";
import useObtenerSubsecciones from "../js/translations/useObtenerSubsecciones";
import { Book } from "../components/shared/book";
import { ASSETS_URL } from "../components/Store";
import { store } from "../components/Store";
import { useHistory } from "react-router-dom";
import { useFavoritesManager } from "../hooks/use-favorites-manager";
import { useQueryParams } from "../hooks/useQueryParams";
import { usePagination } from "../hooks/usePagination";
import { PaginationControls } from "../components/shared/pagination-controls";
import { useBooksPerPage } from "./hooks/booksPerPage";

/**
 * Componente BooksSection
 * Representa una sección de libros páginados.
 * @param {Object} props - Propiedades del componente.
 * @param {Array} props.books - Array de libros.
 * @param {JSX.Element} props.sortDropdownSlot - Slot para el dropdown de ordenamiento.
 * @param {boolean} props.showHeader - Indica si se debe mostrar el encabezado de la sección.
 * @param {boolean} props.shouldShowPagination - Indica si se debe mostrar la paginación.
 * @returns {JSX.Element} Elemento de la sección de libros.
 */
export const BooksSection = ({ books, sortDropdownSlot, showHeader = true, shouldShowPagination=true }) => {
	const memoBooks = React.useMemo(() => (Array.isArray(books) ? books : []), [books]);
	const { textosSubSecciones: textosBusqueda } = useObtenerSubsecciones("busqueda");
	const globalState = useContext(store);
	const isAuthenticatedUser = globalState?.state?.sesionActiva === "SI";
	const favoritesManager = useFavoritesManager();
	const favorites = favoritesManager.favoriteBooks;
	const history = useHistory();
	const params = useQueryParams();
	const defaultPage = params?.get("page") ? Number(params?.get("page")) : 1;
	const numberBooks = useBooksPerPage();
	const pagination = usePagination({
		itemsPerPage: numberBooks, //16
		defaultPage,
		data: memoBooks,
		onChangePage: (page) => {
			scrollToTop();
			params.set("page", page);
			const search = params.toString();
			history.replace({ pathname: "/busqueda", search: search });
		},
	});

	const currentPageData = pagination?.currentPageData;

	const scrollToTop = () => {
		window.scrollTo({
			top: 0,
			behavior: "instant",
		});
	};

	return (
		<>
			{showHeader && (
				<div className="d-lg-flex mb-5">
					<div className="col-lg-6 px-0 d-flex align-items-center">
						<h3 tabIndex={0} className="focusable-secondary blue-dark--color">
							{`${textosBusqueda?.libros?.resultados?.titulo || "Resultados"} (${
								memoBooks.length || 0
							})`}
						</h3>
					</div>

					<div className="col-lg-6 px-0 d-flex justify-content-end mt-3 mt-lg-0">
						{sortDropdownSlot}
					</div>
				</div>
			)}

			<div
				style={{ overflow: "visible" }}
				className="row px-0"
			>
				{currentPageData.map((item, index) => {
					return (
						<div
							className={`cover-book search-col-book`}
							key={`libro-${item?.idLibro || index}`}
						>
							<div className="d-flex justify-content-center">
								<Book
									libro={{
										...item,
										portada: `${ASSETS_URL}${item?.portada}`,
										title:
											item.nombreLibro ||
											item?.titulo ||
											item?.title ||
											item?.nombre ||
											"N/A",
									}}
									alSeleccionar={() => history.push(item?.webUrl)}
									ocultarOpciones={{
										checkbox: true,
										bookType: false,
										botonFavoritos: isAuthenticatedUser ? false : true,
									}}
									alAgregarFav={(item) =>
										favoritesManager.add({ bookId: item?.idLibro })
									}
									alRemoverFav={(item) =>
										favoritesManager.remove({
											bookId: item?.idLibro,
										})
									}
									estadoInicial={{
										esFavorito: favorites.some(
											(fav) => fav?.idLibro === item?.idLibro
										),
									}}
								/>
							</div>
						</div>
					);
				})}
			</div>
			{shouldShowPagination && memoBooks.length > 0 && pagination.totalPages > 1 && (
				<PaginationControls
					onClickNext={() => pagination.nextPage()}
					onClickPrev={() => pagination.prevPage()}
					onClickPage={(page) => pagination.goToPage(page)}
					currentPage={pagination.currentPage}
					totalPages={pagination.totalPages}
					range={pagination.range}
				/>
			)}
		</>
	);
};
